import { GraduationCap, HandHeart, Home, Leaf, Sparkles } from "lucide-react";
import AboutMePhotos from "@/components/AboutMePhotos";

const badges = [
  { icon: HandHeart, label: "Woman of Faith" },
  { icon: GraduationCap, label: "Educator" },
  { icon: Sparkles, label: "Autism Advocate" },
  { icon: Home, label: "Interior Decorator" },
  { icon: Leaf, label: "Landscaping CEO" },
];

export default function WhySection() {
  return (
    <section
      data-testid="why-section"
      id="about"
      className="relative bg-gradient-to-b from-brand-cream via-[#F8F1E5] to-brand-cream py-24 lg:py-32 overflow-hidden"
    >
      {/* Corner florals */}
      <img
        src="/assets/flower_bluebell.png"
        alt=""
        aria-hidden="true"
        className="absolute -top-6 -left-6 w-56 lg:w-72 opacity-85 animate-float-slow pointer-events-none hidden md:block"
      />
      <img
        src="/assets/flower_fern.png"
        alt=""
        aria-hidden="true"
        className="absolute -bottom-6 -right-6 w-56 lg:w-72 opacity-85 animate-drift pointer-events-none hidden md:block"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 z-10 grid lg:grid-cols-12 gap-14 items-start">
        {/* Family photos with Ken Burns zoom + cross-fade + floating thumbnails */}
        <div className="lg:col-span-5 relative pt-4 lg:pt-8">
          <AboutMePhotos />

          {/* Founder identity card overlay */}
          <div className="mt-10 lg:mt-8 bg-brand-cream/95 backdrop-blur-md border border-brand-moss/15 rounded-2xl px-5 py-4 shadow-xl max-w-[280px] mx-auto rotate-[-1.5deg]">
            <p className="font-script text-2xl text-[#B7574E] leading-none">Chatoria N. Franklin</p>
            <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-brand-moss/70">
              Founder · Mother · Educator
            </p>
          </div>
        </div>

        {/* Story (right) */}
        <div className="lg:col-span-7">
          <span className="text-xs uppercase tracking-[0.4em] text-[#B7574E]">
            About Me · Chatoria N. Franklin
          </span>
          <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-brand-moss leading-[1.1] tracking-tight">
            Handcrafted <span className="italic">with love</span>,
            <br />
            made for you. <span className="text-brand-gold">♡</span>
          </h2>

          <div className="mt-8 space-y-5 text-brand-ink/85 text-[17px] leading-relaxed max-w-2xl">
            <p>
              A woman of faith, she lives with purpose, grace, and an unwavering commitment to
              serving others. As an <span className="font-semibold text-brand-moss">educator</span>, she is passionate about shaping young minds
              and creating meaningful, inclusive learning experiences.
            </p>
            <p>
              Her advocacy for <span className="font-semibold text-[#B7574E]">autism</span> is deeply personal — she is the proud mother of a
              daughter with autism — and she uses her voice to champion awareness, acceptance, and
              support for families within her community.
            </p>
            <p>
              She is the <span className="font-semibold text-brand-moss">CEO and Founder of Stars of Autism</span>, where she leads with vision
              and heart, creating opportunities, resources, and outreach that uplift individuals on
              the autism spectrum. She is also the <span className="font-semibold text-brand-moss">CEO and Founder of Fearfully Wonderfully
              Made Interior Decorating, LLC</span>, blending creativity and purpose to design spaces that
              inspire, comfort, and reflect identity. In addition, she serves as the{" "}
              <span className="font-semibold text-brand-moss">CEO of Mid-South Lawn Service, LLC</span>, bringing that same intention to
              outdoor spaces — cultivating beauty from the ground up.
            </p>
            <p>
              Beyond her professional and advocacy work, she finds joy in creativity, embracing her
              love for crafts as both a passion and a meaningful outlet. Born{" "}
              <span className="font-semibold text-brand-moss">October 9, 1982</span>, she embodies strength, resilience, and compassion. Above
              all, she is a devoted mother whose life is grounded in faith and driven by love,
              purpose, and lasting impact.
            </p>
          </div>

          {/* Role badges */}
          <div className="mt-10 flex flex-wrap gap-3">
            {badges.map((b) => {
              const Icon = b.icon;
              return (
                <span
                  key={b.label}
                  data-testid={`why-badge-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-2 bg-brand-cream border border-brand-moss/20 rounded-full px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-moss shadow-sm"
                >
                  <Icon className="w-4 h-4 text-brand-gold" strokeWidth={1.7} />
                  {b.label}
                </span>
              );
            })}
          </div>

          <blockquote className="mt-10 pl-6 border-l-4 border-brand-gold">
            <p className="font-serif italic text-2xl text-brand-moss leading-relaxed">
              &ldquo;And who knows but that you have come to your royal position for such a time as this?&rdquo;
            </p>
            <cite className="mt-2 block text-xs uppercase tracking-[0.35em] text-[#B7574E] not-italic">
              Esther 4:14
            </cite>
          </blockquote>

          {/* Stars of Autism — Meet the Advocate tile */}
          <a
            href="https://starsofautism.org/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="stars-of-autism-tile"
            className="group relative block mt-10 overflow-hidden rounded-3xl bg-gradient-to-br from-[#3D2E5C] via-[#5B4890] to-[#7A5FB6] p-7 lg:p-8 text-brand-cream shadow-xl hover:-translate-y-1 hover:shadow-2xl transition-all duration-500"
          >
            {/* soft twinkle stars */}
            <span className="absolute top-4 right-8 text-brand-gold text-lg animate-float-slow" aria-hidden="true">✦</span>
            <span className="absolute top-10 right-16 text-brand-gold/70 text-xs animate-drift" aria-hidden="true">✦</span>
            <span className="absolute bottom-8 right-24 text-brand-gold/60 text-sm animate-float-slow" style={{ animationDelay: "0.8s" }} aria-hidden="true">✦</span>
            <span className="absolute bottom-14 right-6 text-brand-gold text-base animate-drift" style={{ animationDelay: "1.4s" }} aria-hidden="true">✦</span>

            <div className="relative flex items-start gap-5">
              <span className="flex-shrink-0 flex items-center justify-center w-16 h-16 rounded-full bg-brand-cream/10 border border-brand-gold/40 backdrop-blur-sm">
                <span className="font-serif text-3xl text-brand-gold leading-none">★</span>
              </span>
              <div className="flex-1 min-w-0">
                <span className="text-[10px] uppercase tracking-[0.35em] text-brand-gold">
                  Meet the Advocate
                </span>
                <h3 className="mt-2 font-serif text-2xl lg:text-3xl leading-tight">
                  Chatoria also leads{" "}
                  <span className="italic text-brand-gold">Stars of Autism</span>.
                </h3>
                <p className="mt-3 text-sm text-brand-cream/80 leading-relaxed max-w-md">
                  A non-profit championing awareness, acceptance, and support for individuals on
                  the autism spectrum and the families who love them.
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm text-brand-gold border-b border-brand-gold/50 pb-0.5 group-hover:gap-3 transition-all">
                  Visit starsofautism.org
                  <span aria-hidden="true">→</span>
                </span>
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
